import React from "react";
import { toast as shellToast } from "../../shell/toast";
import { DataSourceStrip } from "../../shell/opSource";
import { I, Icon, Modal, SegmentedControl, JrnEyeOff } from "./jrn-ui";
import { Btn, T } from "@v0y4ge/jrni-ui";
import { deepMerge, journeyScreens, journeyURL, baseURL, type Journey, type Patch } from "./journey-model";
import JourneyRenderer from "./JourneyRenderer";
import { PaneBoundary } from "../../shell/ErrorBoundary";
import { useT } from "@v0y4ge/i18n-runtime";
import {
  JDesignPanel, JHeaderFooterPanel, JFlowPanel, JChatPanel, JHubPanel, JEventsPanel,
  JServicesPanel, JLocationsPanel, JStaffPanel, JTimePanel, JQueuePanel, JDetailsPanel,
  JConfirmPanel, JTicketPanel, JTextPanel, JSharePanel,
} from "./journey-panels";
type Toastish = { tone?: "success" | "danger" | "warn" | "info"; title: string; sub?: string };
const toast = (o: Toastish) => { try { shellToast({ tone: o.tone, title: o.title, subtitle: o.sub }); } catch { /* toast host absent in preview */ } };

/* Journey RECORDS are dynamic config (`Journey = any`, journey-model), but the
 * editor's own catalogs and props have knowable shapes — typed here so the
 * strict gate has something real to check. */
interface JbItem { id: string; label: string; icon: string; types?: string[]; step?: string; qstep?: string; estep?: string }
interface JbGroup { group: string; items: JbItem[] }
interface PublishCheck { ok: unknown; warn?: boolean; label: string; sub: string }
interface JourneyField { id: string; label: string; type: string; required?: boolean; locked?: boolean }

// Journey editor shell — section nav, config panels, sticky live preview,
// full-screen preview modal, and the publish flow.

const JB_SECTIONS: JbGroup[] = [
  { group: "Look & feel", items: [
    { id: "design", label: "Design & theme", icon: "pencil" },
    { id: "headerfooter", label: "Header & footer", icon: "browser" },
  ]},
  { group: "Flow", items: [
    { id: "flow", label: "Flow & order", icon: "network-wired", types: ["appointment", "queue"] },
    { id: "chat", label: "Conversation", icon: "sparkle", types: ["chat"] },
    { id: "hublayout", label: "Hub layout", icon: "network-wired", step: "hub", types: ["hub"] },
  ]},
  { group: "Booking steps", items: [
    { id: "services", label: "Services", icon: "tags", step: "service", types: ["appointment", "queue", "chat", "hub"] },
    { id: "locations", label: "Locations", icon: "network-wired", step: "location", types: ["appointment", "queue", "chat", "hub"] },
    { id: "events", label: "Events", icon: "calendar", step: "hub", estep: "events", types: ["hub", "event"] },
    { id: "staff", label: "Staff selection", icon: "user-friends", step: "staff", types: ["appointment", "chat", "hub"] },
    { id: "time", label: "Time & availability", icon: "clock", step: "time", types: ["appointment", "hub"] },
    { id: "queue", label: "Queue & wait", icon: "clock", step: "location", types: ["queue", "hub"] },
    { id: "details", label: "Details & consent", icon: "user", step: "details", qstep: "join" },
    { id: "confirm", label: "Confirmation", icon: "check", step: "confirm", types: ["appointment", "hub"] },
    { id: "ticket", label: "Live ticket", icon: "check", step: "ticket", types: ["queue", "hub"] },
  ]},
  { group: "Content", items: [
    { id: "text", label: "On-screen text", icon: "edit", types: ["appointment", "queue", "event"] },
  ]},
  { group: "Launch", items: [
    { id: "share", label: "Share & embed", icon: "browser" },
  ]},
];
function jbSections(type?: string): JbGroup[] {
  const t = type || "appointment";
  return JB_SECTIONS.map((g) => ({ ...g, items: g.items.filter((it) => !it.types || it.types.includes(t)) })).filter((g) => g.items.length);
}

export default function JourneyEditor({ journey, onChange, onBack }: { journey: Journey; onChange: (next: Journey) => void; onBack: () => void }) {
  const t = useT();
  const [section, setSection] = React.useState("design");
  const [device, setDevice] = React.useState("desktop");
  const [dirty, setDirty] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [unpubOpen, setUnpubOpen] = React.useState(false);
  const [previewKey, setPreviewKey] = React.useState(0);

  const set = (patch: Patch) => { onChange({ ...deepMerge(journey, patch), _edited: true }); setDirty(true); };
  const sections = jbSections(journey.type);
  const allItems = sections.flatMap((g) => g.items);
  const cur: Partial<JbItem> = allItems.find((s) => s.id === section) || {};
  const focusStep = journey.type === "queue" && cur.qstep ? cur.qstep : journey.type === "event" && cur.estep ? cur.estep : cur.step;

  const publish = () => {
    onChange({ ...deepMerge(journey, { status: "published", publishedAt: "Just now", updatedAt: "Just now" }), _edited: true });
    setDirty(false); setPublishOpen(false);
    toast({ title: "Journey published", sub: journeyURL(journey) + " is live" });
  };
  const unpublish = () => {
    onChange({ ...journey, status: "draft", publishedAt: null, updatedAt: "Just now", _edited: true });
    setUnpubOpen(false);
    toast({ title: "Journey taken offline", sub: journeyURL(journey).replace("https://", "") + " · link paused" });
  };

  const statusBadge = journey.status === "published"
    ? (dirty ? <span className="badge badge-warning">{t('journeys.editor.text6')}</span> : <span className="badge badge-success"><span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", display: "inline-block", marginRight: 5, animation: "pulse 1.8s infinite" }} />{t('journeys.editor.text7')}</span>)
    : <span className="badge badge-soft">{t('journeys.editor.text8')}</span>;

  return (
    <div className="col" style={{ padding: "0 32px 48px", gap: 14 }} data-screen-label="Journey editor">
      <DataSourceStrip screen="journeys-editor" ops={["getJourney", "updateJourney", "setJourneyStatus", "getTenantBrand", "setTenantBrand", "scanBrandFromWebsite"]} reqId="REQ-063" />

      {/* ── Editor header ── */}
      <div className="jb-editor-head row" style={{ gap: 10, alignItems: "center", padding: "14px 0 12px", borderBottom: "1px solid var(--jrni-color-neutral-5)" }}>
        <Btn variant="ghost" size="sm" icon={<I.ChevronL size={12} />} onClick={onBack}>{t('journeys.editor.text9')}</Btn>
        <div style={{ width: 1, height: 20, background: "var(--jrni-color-neutral-5)" }} />
        {/* ui-raw: inline auto-sizing title editor — borderless until focus, char-width autosize; the shared Input would impose a bordered fixed-height row */}
        <input value={journey.name} onChange={(e) => set({ name: e.target.value })} aria-label={t('journeys.editor.ariaLabel')}
               style={{ fontFamily: "inherit", fontSize: 17, fontWeight: 700, color: "var(--jrni-color-text-strong)", border: "1px solid transparent", borderRadius: 6, padding: "4px 8px", background: "transparent", minWidth: 60, flexShrink: 0, width: (journey.name.length + 6) + "ch", maxWidth: 380 }}
               onFocus={(e) => e.target.style.borderColor = "var(--jrni-color-primary-1)"} onBlur={(e) => e.target.style.borderColor = "transparent"} />
        {statusBadge}
        <div style={{ marginLeft: "auto" }} className="row gap-2">
          <span style={{ fontSize: 11, color: "var(--jrni-color-text-soft)", whiteSpace: "nowrap" }}>{dirty ? t('journeys.editor.text10') : journey.status === "published" ? /* TODO(i18n): replace template-literal with t('journeys.editor.text11', { ... }) */`Published ${journey.publishedAt}` : /* TODO(i18n): replace template-literal with t('journeys.editor.text12', { ... }) */`Edited ${journey.updatedAt}`}</span>
          <Btn variant="secondary" size="sm" icon={<I.Browser size={13} />} onClick={() => setPreviewOpen(true)}>{t('journeys.editor.text13')}</Btn>
          {journey.status === "published" && <Btn variant="secondary" size="sm" title={t('journeys.editor.title3')} icon={<JrnEyeOff size={13} />} onClick={() => setUnpubOpen(true)}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = "var(--jrni-color-semantic-orange-1)"; e.currentTarget.style.borderColor = "var(--jrni-color-semantic-orange-1)"; }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = ""; e.currentTarget.style.borderColor = ""; }}>{t('journeys.editor.text14')}</Btn>}
          <Btn size="sm" onClick={() => setPublishOpen(true)}>{journey.status === "published" ? t('journeys.editor.text15') : t('journeys.editor.text16')}</Btn>
        </div>
      </div>

      {/* ── Body: nav | panel | preview ── */}
      <div className="jb-editor-body">
        <nav className="jb-editor-nav">
          {sections.map((g) => (
            <React.Fragment key={g.group}>
              <div className="jb-nav-group" style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)", padding: "10px 10px 4px" }}>{g.group}</div>
              {g.items.map((it) => {
                const active = section === it.id;
                return (
                  // ui-raw: bespoke section-nav item — persistent active state (p4 bg / p1 text), not a standard button
                  <button key={it.id} onClick={() => setSection(it.id)} style={{
                    display: "flex", gap: 9, alignItems: "center", padding: "8px 10px", flexShrink: 0,
                    fontFamily: "inherit", fontSize: 12.5, fontWeight: active ? 700 : 500, textAlign: "left", whiteSpace: "nowrap",
                    color: active ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
                    background: active ? "var(--jrni-color-primary-4)" : "transparent",
                    border: 0, borderRadius: "var(--jrni-radius-sm)", cursor: "pointer",
                  }}><Icon name={it.icon} size={13} />{it.label}</button>
                );
              })}
            </React.Fragment>
          ))}
        </nav>

        <div key={section} className="col" style={{ gap: 14, minWidth: 0 }}>
          {section === "design" && <JDesignPanel j={journey} set={set} />}
          {section === "headerfooter" && <JHeaderFooterPanel j={journey} set={set} />}
          {section === "flow" && <JFlowPanel j={journey} set={set} />}
          {section === "chat" && <JChatPanel j={journey} set={set} />}
          {section === "hublayout" && <JHubPanel j={journey} set={set} />}
          {section === "events" && <JEventsPanel j={journey} set={set} />}
          {section === "services" && <JServicesPanel j={journey} set={set} />}
          {section === "locations" && <JLocationsPanel j={journey} set={set} />}
          {section === "staff" && <JStaffPanel j={journey} set={set} />}
          {section === "time" && <JTimePanel j={journey} set={set} />}
          {section === "queue" && <JQueuePanel j={journey} set={set} />}
          {section === "details" && <JDetailsPanel j={journey} set={set} />}
          {section === "confirm" && <JConfirmPanel j={journey} set={set} />}
          {section === "ticket" && <JTicketPanel j={journey} set={set} />}
          {section === "text" && <JTextPanel j={journey} set={set} />}
          {section === "share" && <JSharePanel j={journey} set={set} onPreview={() => setPreviewOpen(true)} />}
        </div>

        <div className="jb-editor-preview col" style={{ gap: 8 }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--jrni-color-text-soft)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{t('journeys.editor.text17')}</span>
            <div className="row gap-2" style={{ alignItems: "center" }}>
              <Btn variant="ghost" size="sm" title={t('journeys.editor.title4')} icon={<I.Undo size={12} />} onClick={() => setPreviewKey((k) => k + 1)}>{t('journeys.editor.text18')}</Btn>
              <SegmentedControl value={device} onChange={setDevice} options={[{ id: "desktop", label: "Desktop" }, { id: "mobile", label: "Mobile" }]} />
            </div>
          </div>
          <PaneBoundary scope="journeys-editor:preview" label="Journey preview" resetKey={`${journey.id}:${previewKey}`}>
            <JourneyRenderer key={previewKey} journey={journey} device={device} focusStep={focusStep} />
          </PaneBoundary>
          <span style={{ fontSize: 10.5, color: "var(--jrni-color-text-soft)", textAlign: "center" }}>{t('journeys.editor.text19')}</span>
        </div>
      </div>

      {previewOpen && <JourneyPreviewModal journey={journey} onClose={() => setPreviewOpen(false)} onPublish={() => { setPreviewOpen(false); setPublishOpen(true); }} />}
      {publishOpen && <JourneyPublishDialog journey={journey} dirty={dirty} onClose={() => setPublishOpen(false)} onPublish={publish} />}
      {unpubOpen && (
        <Modal title={t('journeys.editor.title5')} subtitle={journey.name} width={460} onClose={() => setUnpubOpen(false)}
               footer={<div className="row" style={{ justifyContent: "flex-end", gap: 8, width: "100%" }}>
                 <Btn variant="secondary" onClick={() => setUnpubOpen(false)}>{t('journeys.editor.text20')}</Btn>
                 <Btn style={{ background: "var(--jrni-color-semantic-orange-1)", borderColor: "var(--jrni-color-semantic-orange-1)" }} icon={<JrnEyeOff size={13} />} onClick={unpublish}>{t('journeys.editor.text14')}</Btn>
               </div>}>
          <div className="col" style={{ gap: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: "var(--jrni-color-text-body)", lineHeight: 1.6, textWrap: "pretty" }}>
              <b>{journey.name}</b> {t('journeys.editor.text21')}</p>
            <span className="mono" style={{ fontSize: 11.5, color: "var(--jrni-color-primary-1)" }}>{journey.domain || baseURL}/{journey.slug}</span>
            <div style={{ padding: "9px 12px", borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-primary-4)", color: "var(--jrni-color-text-body)", fontSize: 11.5, lineHeight: 1.5 }}>{t('journeys.editor.text22')}</div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Full-screen preview (pre-publish check) ─────────────────────────
function JourneyPreviewModal({ journey, onClose, onPublish }: { journey: Journey; onClose: () => void; onPublish: () => void }) {
  const t = useT();
  const [device, setDevice] = React.useState("desktop");
  const [key, setKey] = React.useState(0);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(20,22,30,0.68)", backdropFilter: "blur(3px)", display: "flex", flexDirection: "column", padding: "18px clamp(10px, 4vw, 48px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ margin: "0 auto", width: "100%", maxWidth: device === "mobile" ? 460 : 1000, display: "flex", flexDirection: "column", gap: 10, minHeight: 0, flex: 1 }}>
        <div className="row" style={{ alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: T.onColor, fontSize: 13.5, fontWeight: 700 }}>{t('journeys.editor.text23')}{journey.name}</span>
          <span className="badge badge-soft" style={{ background: "rgba(255,255,255,0.14)", color: T.onColor, border: 0 }}>{t('journeys.editor.text24')}</span>
          <div style={{ marginLeft: "auto" }} className="row gap-2">
            <Btn variant="secondary" size="sm" style={{ background: "rgba(255,255,255,0.12)", color: T.onColor, borderColor: "rgba(255,255,255,0.25)" }} icon={<I.Undo size={12} />} onClick={() => setKey((k) => k + 1)}>{t('journeys.editor.text18')}</Btn>
            <div style={{ display: "flex", borderRadius: 7, overflow: "hidden", border: "1px solid rgba(255,255,255,0.25)" }}>
              {["desktop", "mobile"].map((d) => (
                // ui-raw: bespoke device toggle on the fixed dark preview scrim — theme-independent white, not a light-surface control
                <button key={d} onClick={() => setDevice(d)} style={{ padding: "6px 12px", fontSize: 11.5, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", border: 0, background: device === d ? T.onColor : "transparent", color: device === d ? "var(--jrni-color-text-strong)" : T.onColor }}>{d === "desktop" ? t('journeys.editor.text25') : t('journeys.editor.text26')}</button>
              ))}
            </div>
            <Btn size="sm" onClick={onPublish}>{journey.status === "published" ? t('journeys.editor.text15') : t('journeys.editor.text16')}</Btn>
            <Btn variant="secondary" size="sm" iconOnly={<I.X size={13} />} style={{ background: "rgba(255,255,255,0.12)", color: T.onColor, borderColor: "rgba(255,255,255,0.25)" }} onClick={onClose} />
          </div>
        </div>
        <div style={{ overflowY: "auto", minHeight: 0, borderRadius: 14 }}>
          <PaneBoundary scope="journeys-editor:preview-full" label="Journey preview" resetKey={`${key}:${device}`}>
            <JourneyRenderer key={key + device} journey={journey} device={device} />
          </PaneBoundary>
        </div>
      </div>
    </div>
  );
}

// ─── Publish dialog with pre-flight checklist ────────────────────────
function JourneyPublishDialog({ journey, dirty, onClose, onPublish }: { journey: Journey; dirty: boolean; onClose: () => void; onPublish: () => void }) {
  const t = useT();
  const screens = journeyScreens(journey);
  const isQ = journey.type === "queue", isChat = journey.type === "chat", isHub = journey.type === "hub", isE = journey.type === "event";
  const d = journey.steps.details;
  const c = journey.steps.confirm || {};
  const tk = journey.steps.ticket || {};
  const customized = Object.keys(journey.text).filter((k) => journey.text[k] != null).length;
  const selfServe: PublishCheck = isE
    ? { ok: true, label: (journey.steps.events && journey.steps.events.waitlist === false) ? "Full events show as closed" : "Full events roll to a waitlist", sub: ["event page with host & capacity", (journey.steps.events && journey.steps.events.waitlist === false) ? null : "waitlist when sold out", "add to calendar on confirmation"].filter(Boolean).join(" · ") }
    : isHub
    ? { ok: true, label: "Three engagements in one journey", sub: "Appointments, event reservations and the live queue — all from the location hub." }
    : isChat
    ? { ok: true, label: "Conversational booking", sub: "The assistant settles service, place, time and specialist before confirming — typed input is AI-understood." }
    : isQ
    ? { ok: tk.allowLeave || tk.offerAppointment, warn: !(tk.allowLeave || tk.offerAppointment), label: "Self-service on the ticket", sub: [tk.showPosition && "live position", tk.allowLeave && "leave queue", tk.offerAppointment && "book-instead"].filter(Boolean).join(" · ") || "No ticket actions enabled" }
    : { ok: c.showReschedule || c.showCancel || c.showAddToCalendar, warn: !(c.showReschedule || c.showCancel || c.showAddToCalendar), label: "Self-service after booking", sub: [c.showAddToCalendar && "add to calendar", c.showReschedule && "reschedule", c.showCancel && "cancel"].filter(Boolean).join(" · ") || "No confirmation actions enabled" };
  const checks: PublishCheck[] = [
    { ok: true, label: `${screens.length} step${screens.length === 1 ? "" : "s"} in the flow`, sub: screens.map((s) => ({ service: "Services", location: "Location", staff: "Staff", time: "Time", details: "Details", confirm: "Confirmation", join: "Join details", ticket: "Live ticket", chat: "Conversation — assistant-led", hub: "What's on", events: "Browse events", event: "Event page", eventdone: "Event confirmed" })[s]).join(" → ") },
    { ok: true, label: "Required fields cover the customer record", sub: (d.fields as JourneyField[]).filter((f) => f.required).map((f) => f.label).join(", ") },
    { ok: d.consent.required, warn: !d.consent.required, label: d.consent.required ? "Consent required before " + (isQ ? "joining" : isE ? "reserving" : "booking") : "Consent is optional", sub: d.consent.required ? `Policy at ${d.consent.policyUrl}` : "Customers can " + (isQ ? "join" : isE ? "reserve" : "book") + " without accepting the privacy statement — check this is intended." },
    selfServe,
    { ok: true, label: customized ? `${customized} text ${customized === 1 ? "string" : "strings"} customized` : "Default on-screen text", sub: "Everything else uses platform defaults." },
  ];
  return (
    <Modal title={journey.status === "published" ? "Publish changes" : "Publish journey"} subtitle={journeyURL(journey)} onClose={onClose}
           footer={<div className="row gap-2" style={{ justifyContent: "flex-end", width: "100%" }}>
             <Btn variant="secondary" onClick={onClose}>{t('journeys.editor.text27')}</Btn>
             <Btn onClick={onPublish}>{journey.status === "published" ? t('journeys.editor.text15') : t('journeys.editor.text28')}</Btn>
           </div>}>
      <div className="col" style={{ gap: 4 }}>
        {checks.map((ch, i) => (
          <div key={i} className="row" style={{ gap: 10, padding: "9px 2px", alignItems: "flex-start", borderBottom: i < checks.length - 1 ? "1px solid var(--jrni-color-neutral-6)" : 0 }}>
            <span style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", background: ch.warn ? T.orange2 : T.green2, color: ch.warn ? T.orange1 : T.green1 }}>{ch.warn ? <I.Warning size={11} /> : <I.Check size={11} />}</span>
            <div className="col" style={{ gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--jrni-color-text-strong)" }}>{ch.label}</span>
              <span style={{ fontSize: 11.5, color: "var(--jrni-color-text-soft)", lineHeight: 1.5 }}>{ch.sub}</span>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-primary-4)", fontSize: 11.5, color: "var(--jrni-color-text-body)", lineHeight: 1.55 }}>
          {journey.status === "published" ? t('journeys.editor.text29') : t('journeys.editor.text30')}
        </div>
      </div>
    </Modal>
  );
}


